import React from "react";
import type { StatusFilter, RedirectFilter, SkipFilter } from "./utils";

interface MigrationFiltersProps {
	searchInput: string;
	onSearchInputChange: (value: string) => void;
	statusFilter: StatusFilter;
	onStatusFilterChange: (value: StatusFilter) => void;
	redirectFilter: RedirectFilter;
	onRedirectFilterChange: (value: RedirectFilter) => void;
	skipFilter: SkipFilter;
	onSkipFilterChange: (value: SkipFilter) => void;
	onApply: () => void;
	onClear: () => void;
}

export const MigrationFilters: React.FC<MigrationFiltersProps> = ({
	searchInput,
	onSearchInputChange,
	statusFilter,
	onStatusFilterChange,
	redirectFilter,
	onRedirectFilterChange,
	skipFilter,
	onSkipFilterChange,
	onApply,
	onClear
}) => {
	return (
		<section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
			<header>
				<p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Filters</p>
			</header>
			<div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
				<div className="flex-1 min-w-[220px]">
					<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Search</label>
					<input
						type="text"
						placeholder="URL, title, heading..."
						className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						value={searchInput}
						onChange={(event) => onSearchInputChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								onApply();
							}
						}}
					/>
				</div>
				<div className="flex flex-col">
					<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
					<div className="mt-1 flex flex-wrap gap-2">
						{(["all", "supported", "broken"] as StatusFilter[]).map((option) => (
							<button
								type="button"
								key={option}
								onClick={() => onStatusFilterChange(option)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
									statusFilter === option ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
								}`}
							>
								{option === "all" ? "All" : option.charAt(0).toUpperCase() + option.slice(1)}
							</button>
						))}
					</div>
				</div>
				<div className="flex flex-col">
					<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Redirected</label>
					<div className="mt-1 flex flex-wrap gap-2">
						{(["all", "redirected"] as RedirectFilter[]).map((option) => (
							<button
								type="button"
								key={option}
								onClick={() => onRedirectFilterChange(option)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
									redirectFilter === option ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
								}`}
							>
								{option === "all" ? "All entries" : "Only redirected"}
							</button>
						))}
					</div>
				</div>
				<div className="flex flex-col">
					<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Skipped</label>
					<div className="mt-1 flex flex-wrap gap-2">
						{(["all", "skipped", "active"] as SkipFilter[]).map((option) => (
							<button
								type="button"
								key={option}
								onClick={() => onSkipFilterChange(option)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
									skipFilter === option ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
								}`}
							>
								{option === "all" ? "All" : option === "skipped" ? "Skipped" : "Active"}
							</button>
						))}
					</div>
				</div>
				<div className="hidden flex-wrap gap-3 md:flex">
					<button
						type="button"
						onClick={onApply}
						className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
					>
						Apply filters
					</button>
					<button
						type="button"
						onClick={onClear}
						className="text-sm font-semibold text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-900"
					>
						Clear all
					</button>
				</div>
			</div>
			<div className="flex flex-wrap items-center gap-3 md:hidden">
				<button
					type="button"
					onClick={onApply}
					className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
				>
					Apply filters
				</button>
				<button
					type="button"
					onClick={onClear}
					className="text-sm font-semibold text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-900"
				>
					Clear all
				</button>
			</div>
		</section>
	);
};
